import React, { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Download, Plus } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { initialResumeState, sampleResumeState } from '../constants/resumeData';
import { ExperienceForm } from '../components/editor/ExperienceForm';
import { EducationForm } from '../components/editor/EducationForm';
import { SkillsForm } from '../components/editor/SkillsForm';
import { CustomSectionForm } from '../components/editor/CustomSectionForm';
import { DraggableSectionList } from '../components/editor/DraggableSectionList';

import { MinimalistTemplate } from '../components/templates/MinimalistTemplate';
import { CreativeTemplate } from '../components/templates/CreativeTemplate';
import { ExecutiveTemplate } from '../components/templates/ExecutiveTemplate';
import { ModernTemplate, ProfessionalTemplate, CompactTemplate, TechTemplate, ElegantTemplate, BoldTemplate } from '../components/templates/TemplateVariants';

// Template Renderer Switcher
const ResumePreview = React.forwardRef(({ data, templateId }, ref) => {
  const renderTemplate = () => {
    switch (templateId) {
      case 'creative': return <CreativeTemplate data={data} />;
      case 'executive': return <ExecutiveTemplate data={data} />;
      case 'modern': return <ModernTemplate data={data} />;
      case 'professional': return <ProfessionalTemplate data={data} />;
      case 'compact': return <CompactTemplate data={data} />;
      case 'tech': return <TechTemplate data={data} />;
      case 'elegant': return <ElegantTemplate data={data} />;
      case 'bold': return <BoldTemplate data={data} />;
      case 'minimal': default: return <MinimalistTemplate data={data} />;
    }
  };

  return (
    <div ref={ref} className="shadow-2xl w-[210mm] min-h-[297mm] mx-auto overflow-hidden bg-white" id="resume-preview">
      {renderTemplate()}
    </div>
  );
});

export const ResumeEditor = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template') || 'minimal';
  const [activeSection, setActiveSection] = useState('personal');
  const [resumeData, setResumeData] = useState(sampleResumeState);

  const previewRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: previewRef,
    documentTitle: `${resumeData.personalInfo.firstName}_Resume`,
  });

  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    setResumeData(prev => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, [name]: value }
    }));
  };

  // Section Management
  const handleSectionReorder = (newOrder) => {
    setResumeData(prev => ({ ...prev, sectionsOrder: newOrder }));
  };

  const addCustomSection = () => {
    const id = `custom_${Date.now()}`;
    const newSection = { id, title: 'New Section', items: [] };
    
    setResumeData(prev => ({
      ...prev,
      customSections: [...prev.customSections, newSection],
      sectionsOrder: [...prev.sectionsOrder, id]
    }));
    setActiveSection(id);
  };

  const updateCustomSection = (id, updatedSection) => {
    setResumeData(prev => ({
      ...prev,
      customSections: prev.customSections.map(s => s.id === id ? updatedSection : s)
    }));
  };

  const deleteCustomSection = (id) => {
     setResumeData(prev => ({
       ...prev,
       customSections: prev.customSections.filter(s => s.id !== id),
       sectionsOrder: prev.sectionsOrder.filter(sid => sid !== id)
     }));
     setActiveSection('personal');
  };

  // Helper to determine what to render in main form area
  const renderActiveForm = () => {
    if (activeSection === 'personal') {
      return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="First Name" name="firstName" value={resumeData.personalInfo.firstName} onChange={handlePersonalInfoChange} />
              <Input label="Last Name" name="lastName" value={resumeData.personalInfo.lastName} onChange={handlePersonalInfoChange} />
              <Input label="Email" name="email" value={resumeData.personalInfo.email} onChange={handlePersonalInfoChange} />
              <Input label="Phone" name="phone" value={resumeData.personalInfo.phone} onChange={handlePersonalInfoChange} />
              <Input label="LinkedIn" name="linkedin" value={resumeData.personalInfo.linkedin} onChange={handlePersonalInfoChange} className="md:col-span-2"/>
              <Input label="Website" name="website" value={resumeData.personalInfo.website} onChange={handlePersonalInfoChange} className="md:col-span-2"/>
              <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Professional Summary</label>
                  <textarea 
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[100px]"
                    name="summary"
                    value={resumeData.personalInfo.summary}
                    onChange={handlePersonalInfoChange}
                  />
              </div>
          </div>
      );
    }

    if (activeSection === 'experience') {
       return <ExperienceForm data={resumeData.experience} onChange={(newExp) => setResumeData(prev => ({ ...prev, experience: newExp }))} />;
    }

    if (activeSection === 'education') {
       return <EducationForm data={resumeData.education} onChange={(newEdu) => setResumeData(prev => ({ ...prev, education: newEdu }))} />;
    }

    if (activeSection === 'skills') {
       return <SkillsForm data={resumeData.skills} onChange={(newSkills) => setResumeData(prev => ({ ...prev, skills: newSkills }))} />;
    }

    // Custom Section
    const customSection = resumeData.customSections.find(s => s.id === activeSection);
    if (customSection) {
       return <CustomSectionForm 
                section={customSection} 
                onChange={(updated) => updateCustomSection(activeSection, updated)} 
                onDeleteSection={() => deleteCustomSection(activeSection)}
              />;
    }

    return <div>Select a section</div>;
  };

  // Prepare list items for Draggable Sort
  // We exclude 'personal' from sortable list usually, keeping it fixed at top, or we can include it.
  // Standard practice: Header/Personal info is fixed. Body sections are reorderable.
  const draggableItems = resumeData.sectionsOrder; 

  return (
    <div className="h-[calc(100vh-64px)] flex overflow-hidden bg-gray-50 pt-4">
      {/* Left Panel - Editor Controls */}
      <div className="w-full md:w-1/2 lg:w-2/5 bg-white border-r border-gray-200 overflow-y-auto flex flex-col">
        <div className="p-6 border-b border-gray-100">
           <div className="flex items-center justify-between mb-4">
             <h2 className="text-2xl font-bold text-gray-800">Editor</h2>
             <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setResumeData(sampleResumeState)}>Load Sample</Button>
                <Button size="sm" variant="secondary" onClick={() => setResumeData(initialResumeState)}>Clear</Button>
             </div>
           </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
            {/* Sidebar Navigation (Reorderable) */}
            <div className="w-1/3 bg-gray-50 border-r border-gray-200 overflow-y-auto p-4 flex flex-col">
                 <button 
                    className={`w-full text-left px-4 py-3 rounded-lg mb-2 text-sm font-medium transition-colors ${activeSection === 'personal' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}
                    onClick={() => setActiveSection('personal')}
                 >
                    Personal Info
                 </button>
                 
                 <div className="mb-4">
                     <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-2">Sections</h3>
                     <DraggableSectionList 
                        items={draggableItems} 
                        onReorder={handleSectionReorder}
                        activeSection={activeSection}
                        onSectionChange={setActiveSection}
                     />
                 </div>

                 <Button onClick={addCustomSection} size="sm" variant="outline" className="mt-auto w-full">
                    <Plus size={16} className="mr-2" /> Add Section
                 </Button>
            </div>

            {/* Form Area */}
            <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-6 pb-2 border-b border-gray-100 capitalize">
                    {activeSection === 'personal' ? 'Personal Information' : 
                     resumeData.customSections.find(s => s.id === activeSection)?.title || activeSection}
                </h3>
                {renderActiveForm()}
            </div>
        </div>
      </div>

      {/* Right Panel - Live Preview */}
      <div className="hidden md:flex flex-1 bg-gray-100 p-8 items-start justify-center overflow-y-auto">
         <div className="relative mt-8 mb-8">
             <div className="absolute -top-16 right-0 flex gap-2">
                 <Button onClick={handlePrint} className="shadow-lg">
                    <Download size={18} className="mr-2" /> Download PDF
                 </Button>
             </div>
            <div className="scale-[0.8] origin-top lg:scale-100 transition-transform">
                <ResumePreview ref={previewRef} data={resumeData} templateId={templateId} />
            </div>
         </div>
      </div>
    </div>
  );
};
